/** Route distances are metres; coordinates are [longitude, latitude]. */
export interface FlowRoutePoint {
  geoPos?: number[];
  trackDist: number;
}
const rad = Math.PI / 180;
const wrap = (angle: number) => ((angle + 540) % 360) - 180;
const valid = (p?: number[]) =>
  p?.length >= 2 && Number.isFinite(p[0]) && Number.isFinite(p[1]) && Math.abs(p[0]) <= 180 && Math.abs(p[1]) <= 90;

/** Project the aircraft onto the nearest main-route segment, without overflight flags. */
export function flowRouteProgress(route: FlowRoutePoint[], position: number[], totalM: number): number | undefined {
  if (!valid(position) || !Number.isFinite(totalM) || totalM <= 0) return undefined;
  let nearest = Infinity;
  let progress: number | undefined;
  for (let i = 1; i < route.length; i++) {
    const a = route[i - 1],
      b = route[i];
    if (
      !valid(a.geoPos) ||
      !valid(b.geoPos) ||
      !Number.isFinite(a.trackDist) ||
      !Number.isFinite(b.trackDist) ||
      a.trackDist >= totalM ||
      b.trackDist <= a.trackDist
    )
      continue;
    const scale = Math.cos(((a.geoPos[1] + b.geoPos[1]) * rad) / 2);
    const x = wrap(b.geoPos[0] - a.geoPos[0]) * scale;
    const y = b.geoPos[1] - a.geoPos[1];
    const px = wrap(position[0] - a.geoPos[0]) * scale;
    const py = position[1] - a.geoPos[1];
    const length2 = x * x + y * y;
    if (length2 === 0) continue;
    const end = Math.min(1, (totalM - a.trackDist) / (b.trackDist - a.trackDist));
    const fraction = Math.max(0, Math.min(end, (px * x + py * y) / length2));
    const distance2 = (px - fraction * x) ** 2 + (py - fraction * y) ** 2;
    if (distance2 < nearest) {
      nearest = distance2;
      progress = Math.max(0, Math.min(totalM, a.trackDist + fraction * (b.trackDist - a.trackDist)));
    }
  }
  return progress;
}

export function flowProgressStyles(progressM: number, totalM: number, withAlternateM: number) {
  const scale = withAlternateM < 30000 ? 0.02 : 0.01;
  const pixels = Math.round(Math.max(0, Math.min(totalM, progressM)) * scale);
  return {
    progress_inner: `height:${pixels}px`,
    progress_notFilled: `display: block;height:${Math.max(0, Math.round(totalM * scale) - pixels)}px`,
  };
}
